
export type SearchParams = Record<string, string | string[] | undefined>;
export const filterLabels = {
  remoteOnly: "Remote services", hasWebsite: "Website", hasEmail: "Email",
  hasPhone: "Phone", hasWhatsapp: "WhatsApp", hasGallery: "Photo gallery",
} as const;
export type SearchFilters = {
  query: string; industry: string; location: string; sort: string; page: number; pageSize: number;
} & Record<keyof typeof filterLabels, boolean>;
export type DiscoverItem = {
  encryptedId: string; slug?: string; title: string; description?: string; imageUrl?: string;
  industry: string; location?: string; isRemote?: boolean;
  website?: string; email?: string; phone?: string; whatsapp?: string;
  isVerified?: boolean; averageRating?: number; reviewCount?: number;
  isOpenNow?: boolean; hasActiveSpecial?: boolean; acceptsBookings?: boolean;
};
export type DiscoverResults = {
  isFuzzyMatch?: boolean;
  payload: SearchFilters; items: DiscoverItem[]; total: number; totalPages: number;
  industries: Array<{ name: string; count: number }>;
};
export type DiscoverService = {
  encryptedId?: string;
  name: string;
  description?: string | null;
  durationMinutes: number;
  price?: number | null;
  currency: string;
  displayOrder?: number;
  /** Populated once the Service table has a Slug column. Until then it's derived from `name`. */
  slug?: string | null;
  /** Photos linked to this service via DiscoverListingMedia.DiscoverListingServiceId. */
  photos?: string[];
  /** Long-form service content (max 2000 chars). */
  content?: string | null;
  isActive?: boolean;
  isBooking?: boolean;
  isProduct?: boolean;
};

export function normalizeFilters(params: SearchParams = {}): SearchFilters {
  const value = (key: string) => (Array.isArray(params[key]) ? params[key][0] : params[key])?.trim() || "";
  const page = Number(value("page"));
  return {
    query: value("query").replace(/\s+/g, " ").slice(0, 200),
    industry: value("industry").slice(0, 200), location: value("location").slice(0, 200),
    sort: ["newest", "name-asc", "name-desc"].includes(value("sort")) ? value("sort") : "relevance",
    page: Number.isSafeInteger(page) && page > 0 ? Math.min(page, 1000000) : 1,
    pageSize: 12,
    ...Object.fromEntries(Object.keys(filterLabels).map(key => [key, ["true", "1", "on"].includes(value(key))])),
  } as SearchFilters;
}

export function searchHref(filters: Partial<SearchFilters> = {}) {
  const params = new URLSearchParams();
  for (const key of ["query", "industry", "location"] as const) if (filters[key]) params.set(key, filters[key]!);
  for (const key of Object.keys(filterLabels) as Array<keyof typeof filterLabels>) if (filters[key]) params.set(key, "true");
  if (filters.sort && filters.sort !== "relevance") params.set("sort", filters.sort);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  return `/discover/search${params.size ? `?${params}` : ""}`;
}

export function slugify(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** The URL segment for a service: DB slug when present, otherwise derived from the name. */
export const serviceSlug = (service: Pick<DiscoverService, "slug" | "name" | "encryptedId">) =>
  service.slug || slugify(service.name) || service.encryptedId || "";

export const serviceHref = (
  company: { slug?: string; encryptedId: string },
  service: Pick<DiscoverService, "slug" | "name" | "encryptedId">,
) => `/discover/${encodeURIComponent(company.slug || company.encryptedId)}/${encodeURIComponent(serviceSlug(service))}`;

/** Resolve a URL segment back to a service. Checks DB slug, then name-derived slug, then encrypted id. */
export function findService(company: { services?: DiscoverService[] }, slug: string) {
  const services = company.services || [];
  return (
    services.find(s => s.slug && s.slug === slug) ||
    services.find(s => slugify(s.name) === slug) ||
    services.find(s => s.encryptedId === slug)
  );
}

export function siteUrl(path = "") {
  const base = (process.env.NEXT_PUBLIC_SITE_URL || "https://www.eyamapp.com").trim().replace(/\/+$/, "");
  return `${base}${path}`;
}

export function webUrl(value?: string): string | undefined {
  if (!value?.trim()) return undefined;
  const candidate = value.trim();
  // Never turn an unsupported URI scheme into a clickable business link.
  if (/^[a-z][a-z\d+.-]*:/i.test(candidate) && !/^https?:\/\//i.test(candidate)) return undefined;
  try {
    const url = new URL(/^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

export function whatsappUrl(value?: string) {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) {
    const url = webUrl(value);
    return url && ["wa.me", "api.whatsapp.com", "web.whatsapp.com", "www.whatsapp.com"].includes(new URL(url).hostname) ? url : undefined;
  }
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith("0")) digits = `27${digits.slice(1)}`;
  return /^\d{7,15}$/.test(digits) ? `https://wa.me/${digits}` : undefined;
}

export const jsonLdString = (data: unknown) => JSON.stringify(data).replace(/</g, "\\u003c");
export const descriptionText = (value: string) => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160);
export function breadcrumbs(entries: Array<{ name: string; path: string }>) {
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: entries.map((entry, i) => ({
    "@type": "ListItem", position: i + 1, name: entry.name, item: siteUrl(entry.path),
  })) };
}
export const directorySchema = () => ({
  "@context": "https://schema.org", "@type": "WebSite", "@id": siteUrl("/#website"), name: "Eyam", url: siteUrl(),
  potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: siteUrl("/discover/search?query={search_term_string}") }, "query-input": "required name=search_term_string" },
});